import moment from 'moment';
import _ from 'lodash';
import * as R from 'ramda';
import Currencies from 'js-money/lib/currency';
import clsx from 'classnames';
import { Intent } from '@blueprintjs/core';
import Currency from 'js-money/lib/currency';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import { isEqual, castArray, isEmpty, includes, pickBy } from 'lodash';
import jsCookie from 'js-cookie';
import intl from 'react-intl-universal';
import { deepMapKeys } from './map-key-deep';
import { NBSP, formatFrenchNumber } from './format';
export * from './deep';

export const getCookie = (name: string, defaultValue?: any) =>
  _.defaultTo(jsCookie.get(name), defaultValue);

export const setCookie = (name: string, value: any, options: any = {}) => {
  const defaultOptions = { expires: 365, path: '/', secure: false };

  // If options is an object, merge with defaults
  // If options is a number (legacy: expiry), convert to new format
  const finalOptions =
    typeof options === 'number'
      ? { ...defaultOptions, expires: options }
      : { ...defaultOptions, ...options };

  jsCookie.set(name, value, finalOptions);
};

export const removeCookie = (name: string) => {
  return jsCookie.remove(name, { path: '/' });
};

export function removeEmptyFromObject(obj: any) {
  obj = Object.assign({}, obj);
  var keys = Object.keys(obj);

  keys.forEach(function (key) {
    const value = obj[key];

    if (value === '' || value === null || value === undefined) {
      delete obj[key];
    }
  });
  return obj;
}

export const optionsMapToArray = (optionsMap: any, service = '') => {
  return Object.keys(optionsMap).map((optionKey) => {
    const optionValue = optionsMap[optionKey];

    return {
      key: service ? `${service}_${optionKey}` : `${optionKey}`,
      value: optionValue,
    };
  });
};

export const optionsArrayToMap = (optionsArray: any[]) => {
  return optionsArray.reduce((map: Record<string, any>, option: any) => {
    map[option.key] = option.value;
    return map;
  }, {});
};

export function numberComma(number: any) {
  number = typeof number === 'number' ? String(number) : number;

  const parts = number.split('.');

  const integer = parts[0] || '0';
  const decimal = parts[1];
  const postfix = decimal ? `.${decimal}` : '';

  return `${integer.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}${postfix}`;
}

export const momentFormatter = (format: string) => {
  return {
    formatDate: (date: any) => moment(date).format(format),
    parseDate: (str: any) => moment(str, format).toDate(),
    placeholder: `${format}`,
  };
};

/** Event handler that exposes the target element's value as a boolean. */
export const handleBooleanChange = (handler: any) => {
  return (event: any) => handler(event.target.checked);
};

/** Event handler that exposes the target element's value as a string. */
export const handleStringChange = (handler: any) => {
  return (event: any) => handler(event.target.value);
};

/** Event handler that exposes the target element's value as a number. */
export const handleNumberChange = (handler: any) => {
  return handleStringChange((value: any) => handler(+value));
};

export const handleDateChange = (handler: any) => {
  return (date: any) => handler(moment(date).format('YYYY-MM-DD'), date);
};

export const objectKeysTransform = (obj: any, transform: any) => {
  return Object.keys(obj).reduce((acc: Record<string, any>, key) => {
    const computedKey = transform(key);
    acc[computedKey] = obj[key];
    return acc;
  }, {});
};

export const compose = (...funcs: any[]) =>
  funcs.reduce(
    (a, b) =>
      (...args: any[]) =>
        a(b(...args)),
    (arg: any) => arg,
  );

export const getObjectDiff = (a: any, b: any) => {
  return _.reduce(
    a,
    (result: string[], value: any, key: any) => {
      return _.isEqual(value, b[key]) ? result : result.concat(key);
    },
    [] as string[],
  );
};

export const parseDateRangeQuery = (keyword: any) => {
  const queries: Record<string, { range: any }> = {
    today: {
      range: 'day',
    },
    this_year: {
      range: 'year',
    },
    this_month: {
      range: 'month',
    },
    this_week: {
      range: 'week',
    },
    this_quarter: {
      range: 'quarter',
    },
  };

  if (typeof queries[keyword] === 'undefined') {
    throw new Error(`The date range query ${keyword} is not defined.`);
  }
  const query = queries[keyword];

  return {
    fromDate: moment().startOf(query.range).toDate(),
    toDate: moment().endOf(query.range).toDate(),
  };
};

export const defaultExpanderReducer = (tableRows: any, level: any) => {
  const expended: any = [];

  const walker = (rows: any[], parentIndex: any = null, currentLevel = 1) => {
    return rows.forEach((row: any, index: number) => {
      const _index = parentIndex ? `${parentIndex}.${index}` : `${index}`;
      expended[_index] = true;

      if (row.children && currentLevel < level) {
        walker(row.children, _index, currentLevel + 1);
      }
    }, {});
  };
  walker(tableRows);
  return expended;
};

// Re-exportes ici : `@/utils` reste le point d'entree unique des formateurs.
export { NBSP, formatFrenchNumber };

/**
 * Formateur monétaire unique du webapp (230+ sites d'appel).
 *
 * Convention produite — la seule du produit : `625 171,96 MAD`
 *  - séparateur de milliers : espace insécable
 *  - séparateur décimal : virgule
 *  - devise en SUFFIXE, séparée par une espace insécable
 *  - signe « moins » en tête : `-3 152 684,50 MAD`
 *
 * @param cents        montant (nombre, ou chaîne éventuellement déjà formatée)
 * @param currencyCode code ISO de la devise (`''` => aucun suffixe)
 * @param props        `noZero` (chaîne vide si 0), `compact` (abrégé k/M/Md)
 */
export function formattedAmount(
  cents: any,
  currencyCode = '',
  props: any = {},
) {
  const currency: any = Currency[currencyCode];

  // Devise inconnue de js-money mais fournie par l'appelant : on retombe sur
  // le code lui-même (jamais de montant affiché « nu » à cause d'un code
  // exotique) avec la précision monétaire usuelle.
  const parsedCurrency = {
    ...currency,
    symbol: currency?.symbol ?? (currencyCode || ''),
    decimal_digits: currency?.decimal_digits ?? (currencyCode ? 2 : 0),
  };
  const parsedProps = {
    noZero: false,
    compact: false,
    ...props,
  };

  if (parsedProps.compact) {
    return formatCompactAmount(
      cents,
      parsedCurrency.symbol,
      parsedProps.noZero,
    );
  }

  const value = toSafeNumber(cents);
  const precision = parsedCurrency.decimal_digits;
  const rounded = Number(Math.abs(value).toFixed(precision));

  if (rounded === 0 && parsedProps.noZero) {
    return '';
  }

  // Le signe est dérivé de la valeur ARRONDIE : -0,004 € s'affiche « 0,00 MAD »
  // et non « -0,00 MAD », qui ferait douter un comptable.
  const sign = value < 0 && rounded !== 0 ? '-' : '';
  const suffix = parsedCurrency.symbol ? `${NBSP}${parsedCurrency.symbol}` : '';

  return `${sign}${formatFrenchNumber(value, precision)}${suffix}`;
}

/**
 * Abrégé lisible pour les cartes d'indicateurs et les axes de graphiques :
 * `32,4 M MAD`. Même convention décimale (virgule) que le format long, et
 * devise en suffixe.
 */
function formatCompactAmount(value: any, symbol = '', noZero = false) {
  const num = Number(value);
  const suffix = symbol ? `${NBSP}${symbol}` : '';

  if (!Number.isFinite(num)) return '';
  if (num === 0) return noZero ? '' : `0${suffix}`;

  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  const trim = (n: number) => formatFrenchNumber(n, 1).replace(/,0$/, '');

  const scaled = (divisor: number, unit: string) =>
    `${sign}${trim(abs / divisor)}${NBSP}${unit}${suffix}`;

  if (abs >= 1_000_000_000) return scaled(1_000_000_000, 'Md');
  if (abs >= 1_000_000) return scaled(1_000_000, 'M');
  if (abs >= 1_000) return scaled(1_000, 'k');

  return `${sign}${formatFrenchNumber(abs, 0)}${suffix}`;
}

/**
 * Nombre sans devise, même convention que `formattedAmount`
 * (« 1 234 » / « 1 234,56 »). `precision` reste pilotable par l'appelant.
 */
export function formattedNumber(amount: any, props: any = {}) {
  const parsedProps = {
    noZero: false,
    precision: 0,
    ...props,
  };
  const value = toSafeNumber(amount);
  const rounded = Number(Math.abs(value).toFixed(parsedProps.precision));

  if (rounded === 0 && parsedProps.noZero) {
    return '';
  }
  const sign = value < 0 && rounded !== 0 ? '-' : '';

  return `${sign}${formatFrenchNumber(value, parsedProps.precision)}`;
}

/**
 * Cours de change : même convention FR, devise en suffixe. Déterministe
 * (plus de dépendance à la locale du navigateur via `Intl`).
 */
export function formattedExchangeRate(amount: any, currency: any) {
  return formattedAmount(amount, currency);
}

export const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
  ...rest
}: any) => (condition ? wrapper({ children, ...rest }) : children);

export const checkRequiredProperties = (obj: any, properties: any[]) => {
  return properties.some((prop: any) => {
    const value = obj[prop];
    return value === '' || value === null || value === undefined;
  });
};

export const saveFilesInAsync = (
  files: any[],
  actionCb: any,
  extraTasks?: any,
): any => {
  files.forEach((file: any) => {
    const formData = new FormData();
    formData.append('attachment', file.file);
    // const oper = new PProgress((resolve, reject, progress) => {
    //   actionCb(formData, file, (requestProgress) => {
    //     progress(requestProgress);
    //   })
    //     .then((data) => {
    //       resolve(data);
    //     })
    //     .catch((error) => {
    //       reject(error);
    //     });
    // });
    // opers.push(oper);
  });
  // return PProgress.all(opers);
};

export const firstLettersArgs = (...args: any[]) => {
  let letters: any[] = [];

  args.forEach((word: any) => {
    if (typeof word === 'string') {
      letters.push(word.charAt(0));
    }
  });
  return letters.join('').toUpperCase();
};

export const uniqueMultiProps = (items: any, props: any) => {
  return _.uniqBy(items, (item: any) => {
    return JSON.stringify(_.pick(item, props));
  });
};

export const transformUpdatedRows = (
  rows: any[],
  rowIndex: number,
  columnIdOrObj: any,
  value: any,
) => {
  const columnId = typeof columnIdOrObj !== 'object' ? columnIdOrObj : null;
  const updateTable = typeof columnIdOrObj === 'object' ? columnIdOrObj : null;
  const newData = updateTable ? updateTable : { [columnId]: value };

  return rows.map((row: any, index: number) => {
    if (index === rowIndex) {
      return { ...rows[rowIndex], ...newData };
    }
    return { ...row };
  });
};

export const tansformDateValue = (date: any) => {
  return moment(date).toDate() || new Date();
};

export const repeatValue = (value: any, len: number) => {
  var arr: any[] = [];
  for (var i = 0; i < len; i++) {
    arr.push(value);
  }
  return arr;
};

export const flatToNestedArray = (
  data: any[],
  config = { id: 'id', parentId: 'parent_id' },
) => {
  const map: Record<string, any> = {};
  const nestedArray: any[] = [];

  data.forEach((item: any) => {
    map[item[config.id]] = item;
    map[item[config.id]].children = [];
  });
  data.forEach((item: any) => {
    const parentItemId = item[config.parentId];

    if (!item[config.parentId]) {
      nestedArray.push(item);
    }
    if (parentItemId && map[parentItemId]) {
      map[parentItemId].children.push(item);
    }
  });
  return nestedArray;
};

export const orderingLinesIndexes = (lines: any[], attribute = 'index') => {
  return lines.map((line: any, index: number) => ({
    ...line,
    [attribute]: index + 1,
  }));
};

export const transformToObject = (arr: any[], key: any) => {
  return arr.reduce(function (acc: Record<string, any>, cur: any, i: number) {
    acc[key ? cur[key] : i] = cur;
    return acc;
  }, {});
};

export const itemsStartWith = (items: any[], char: any) => {
  return items.filter((item: any) => item.indexOf(char) === 0);
};

export const saveInvoke = (func: any, ...rest: any[]) => {
  return func && func(...rest);
};

export const safeInvoke = (func: any, ...rest: any[]) => {
  func && func(...rest);
};

export const transformToForm = (obj: any, emptyInitialValues: any) => {
  return _.pickBy(
    obj,
    (val: any, key: any) =>
      val !== null && Object.keys(emptyInitialValues).includes(key),
  );
};

export function excludePrivateProps(
  obj: Record<string, any>,
): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => !key.startsWith('_')),
  );
}

export function inputIntent({
  error,
  touched,
}: {
  error?: any;
  touched?: any;
}): Intent {
  return error && touched ? Intent.DANGER : Intent.NONE;
}

export function listToTree(
  inputList: any[],
  {
    idFieldKey = 'id',
    parentFieldKey = 'parent_account_id',
    nodeMapper = (node: any) => ({ ...node }),
  }: any,
) {
  var map: Record<string, any> = {},
    node,
    roots = [],
    i;
  const list = inputList.map((item: any) => nodeMapper(item));

  for (i = 0; i < list.length; i += 1) {
    map[list[i][idFieldKey]] = i;
    list[i].children = [];
  }
  for (i = 0; i < list.length; i += 1) {
    node = list[i];

    if (node[parentFieldKey]) {
      list[map[node[parentFieldKey]]].children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function treeToList(
  list: any,
  {
    idFieldKey = 'id',
    childrenFieldKey = 'children',
    nodeMapper = (node: any, depth: any) => node,
    nodeFilter = (node: any, depth: any) => true,
  }: any,
) {
  let depth = 0;

  const walker = (tree: any[]) => {
    return tree.reduce((acc: any[], o: any) => {
      depth += 1;

      if (o[idFieldKey] && nodeFilter(o, depth)) {
        acc.push(nodeMapper(o, depth));
      }
      if (o[childrenFieldKey]) {
        acc = acc.concat(walker(o.children));
      }
      depth -= 1;
      return acc;
    }, []);
  };
  return walker(list);
}

export function defaultToTransform(
  value: any,
  defaultOrTransformedValue: any,
  defaultValue: unknown = undefined,
) {
  const _defaultValue =
    typeof defaultValue === 'undefined'
      ? defaultOrTransformedValue
      : defaultValue;

  const _transfromedValue =
    typeof defaultValue === 'undefined' ? value : defaultOrTransformedValue;

  return value === null ||
    value === undefined ||
    Number.isNaN(value) ||
    value === ''
    ? _defaultValue
    : _transfromedValue;
}

export function isBlank(value: any) {
  return (_.isEmpty(value) && !_.isNumber(value)) || _.isNaN(value);
}

export const getColumnWidth = (
  rows: any[],
  accessor: any,
  {
    maxWidth,
    minWidth,
    magicSpacing = 14,
  }: { maxWidth?: any; minWidth?: any; magicSpacing?: number },
  headerText = '',
) => {
  const cellLength = Math.max(
    ...rows.map((row: any) => (`${_.get(row, accessor)}` || '').length),
    headerText.length,
  );
  let result = cellLength * magicSpacing;

  result = minWidth ? Math.max(minWidth, result) : result;
  result = maxWidth ? Math.min(maxWidth, result) : result;

  return result;
};

export const getForceWidth = (text: string, magicSpacing = 14) => {
  const textLength = text.length;
  const result = textLength * magicSpacing;

  return result;
};

/**
 * Coerce une valeur en nombre fini, en retombant TOUJOURS sur 0 pour une entrée
 * non numérique (le nom « safe » l'implique — et un `NaN` propagé par `safeSumBy`
 * via `_.sum` empoisonnait des totaux entiers). Gère les montants formatés :
 * espaces / espaces insécables (séparateurs de milliers FR) et virgule décimale.
 *
 * Ne modifie AUCUNE valeur déjà correctement convertie (`42`, `'42'`, `'3.14'`,
 * nombres) : seules les entrées qui produisaient jusqu'ici `NaN` changent.
 */
export const toSafeNumber = (number: any) => {
  if (typeof number === 'number') {
    return Number.isFinite(number) ? number : 0;
  }
  if (number === null || number === undefined) return 0;
  if (typeof number !== 'string') {
    const coerced = _.toNumber(number);
    return Number.isFinite(coerced) ? coerced : 0;
  }

  // Retire espaces, espaces insécables (U+00A0) et fines (U+202F) — séparateurs
  // de milliers courants dans l'affichage FR (« 1 234,56 »).
  let s = number.replace(/\s/g, '');
  if (s === '') return 0;

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma > -1 && lastDot > -1) {
    // Les deux présents : le séparateur le plus à droite est le décimal,
    // l'autre est un séparateur de milliers (FR « 1.234,56 » ou EN « 1,234.56 »).
    const decSep = lastComma > lastDot ? ',' : '.';
    const grpSep = decSep === ',' ? '.' : ',';
    s = s.split(grpSep).join('').replace(decSep, '.');
  } else if (lastComma > -1) {
    // Virgule seule → décimale (convention FR).
    s = s.replace(',', '.');
  }

  const parsed = Number(s);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const transformToCamelCase = (object: any): any => {
  return deepMapKeys(object, (key: any) => _.camelCase(key));
};

export const transfromToSnakeCase = (object: any): any => {
  return deepMapKeys(object, (key: any) => _.snakeCase(key));
};

export const transformTableQueryToParams = (object: any) => {
  return transfromToSnakeCase(object);
};

export function flatObject(obj: any) {
  const flatObject: Record<string, any> = {};
  const path: any[] = []; // current path

  function dig(obj: any) {
    if (obj !== Object(obj)) {
      return (flatObject[path.join('.')] = obj);
    }

    for (let key in obj) {
      path.push(key);
      dig(obj[key]);
      path.pop();
    }
  }

  dig(obj);
  return flatObject;
}

export function randomNumber(min: number, max: number) {
  if (min > max) {
    let temp = max;
    max = min;
    min = temp;
  }
  if (min <= 0) {
    return Math.floor(Math.random() * (max + Math.abs(min) + 1)) + min;
  } else {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}

export function transformResponse(response: any) {
  return transformToCamelCase(response);
}

export function transactionNumber(prefix: any, number: any) {
  const codes = [];

  if (prefix) {
    codes.push(prefix);
  }
  if (number) {
    codes.push(number);
  }
  return codes.join('');
}

export function safeCallback(callback: any, ...args: any[]) {
  return () => callback && callback(...args);
}

export const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  isEqual,
);

/**
 * Detarmines whether the table has empty status.
 */
export const isTableEmptyStatus = ({ data, pagination, filterMeta }: any) => {
  return [
    _.isEmpty(data),
    _.isEmpty(filterMeta.view),
    pagination.page === 1,
  ].every((cond) => cond === true);
};

/**
 * Transformes the pagination meta to table props.
 */
export function getPagesCountFromPaginationMeta(pagination: any) {
  const { pageSize, total } = pagination;

  return Math.ceil(total / pageSize);
}

function transformFilterRoles(filterRoles: any) {
  return JSON.stringify(filterRoles);
}

/**
 * Transformes the table state to url query.
 */
export function transformTableStateToQuery(tableState: any) {
  const { pageSize, pageIndex, viewSlug, sortBy } = tableState || {};

  const query = {
    pageSize: pageSize ?? 20,
    // Guard against undefined pageIndex (happens if the redux slice backing
    // this table isn't wired into the root reducer yet). Without the ??, the
    // fetch would end up with `?page=NaN` in the URL.
    page: (pageIndex ?? 0) + 1,
    ...(tableState.filterRoles
      ? {
          stringified_filter_roles: transformFilterRoles(
            tableState.filterRoles,
          ),
        }
      : {}),
    ...(viewSlug ? { viewSlug } : {}),
    ...(Array.isArray(sortBy) && sortBy.length > 0
      ? {
          column_sort_by: sortBy[0].id,
          sort_order: sortBy[0].desc ? 'desc' : 'asc',
        }
      : {}),
  };
  return transfromToSnakeCase(query);
}

/**
 * Transformes the global table state to table state.
 */
export function globalTableStateToTable(globalState: any) {
  return {
    ..._.omit(globalState, ['customViewId']),
  };
}

/**
 * Transformes the pagination meta repsonse.
 */
export function transformPagination(pagination: any) {
  const transformed = transformResponse(pagination);

  return {
    ...transformed,
    pagesCount: getPagesCountFromPaginationMeta(transformed),
  };
}

export function removeRowsByIndex(rows: any[], rowIndex: any) {
  const removeIndex = parseInt(rowIndex, 10);
  const newRows = rows.filter(
    (row: any, index: number) => index !== removeIndex,
  );

  return newRows;
}

export function safeSumBy(entries: any, getter: any) {
  return _.chain(entries)
    .map((row: any) => toSafeNumber(_.get(row, getter)))
    .sum()
    .value();
}

export const fullAmountPaymentEntries = (entries: any[]) => {
  return entries.map((item: any) => ({
    ...item,
    payment_amount: item.due_amount,
  }));
};

export const amountPaymentEntries = (amount: number, entries: any[]) => {
  let total = amount;

  return entries.map((item: any) => {
    const diff = Math.min(item.due_amount, total);
    total -= Math.max(diff, 0);

    return {
      ...item,
      payment_amount: diff,
    };
  });
};

export const updateAutoAddNewLine =
  (defaultEntry: any, props: any[]) => (entries: any[]) => {
    const newEntries = [...entries];
    const lastEntry: any = _.last(newEntries);
    const newLine = props.filter(
      (entryKey: any) => !isBlank(lastEntry[entryKey]),
    );

    return newLine.length > 0 ? [...entries, defaultEntry] : [...entries];
  };

/**
 * Ensure min entries lines.
 * @param {number} min
 * @param {any} defaultEntry
 */
export const updateMinEntriesLines =
  (min: number, defaultEntry: any) => (lines: any[]) => {
    if (lines.length < min) {
      const diffLines = Math.max(min - lines.length, 0);
      return [...lines, ...repeatValue(defaultEntry, diffLines)];
    }
    return lines;
  };

export const updateRemoveLineByIndex = (rowIndex: any) => (entries: any[]) => {
  const removeIndex = parseInt(rowIndex, 10);
  return entries.filter((row: any, index: number) => index !== removeIndex);
};

export const updateTableCell =
  (rowIndex: number, columnId: any, value: any) => (old: any[]) => {
    return old.map((row: any, index: number) => {
      if (index === rowIndex) {
        return {
          ...old[rowIndex],
          [columnId]: value,
        };
      }
      return row;
    });
  };

export const updateTableRow =
  (rowIndex: number, value: any) => (old: any[]) => {
    return old.map((row: any, index: number) => {
      if (index === rowIndex) {
        return {
          ...old[rowIndex],
          ...value,
        };
      }
      return row;
    });
  };
export const transformGeneralSettings = (data: any) => {
  return _.mapKeys(data, (value: any, key: any) => _.snakeCase(key));
};

export const calculateStatus = (
  paymentAmount: number,
  balanceAmount: number,
) => {
  return _.round(paymentAmount / balanceAmount, 2);
};

const getCurrenciesOptions = () => {
  return Object.keys(Currencies).map((currencyCode: any) => {
    const currency: any = Currencies[currencyCode];

    return {
      ...currency,
      currency_code: currencyCode,
      formatted_name: `${currencyCode} - ${currency.name}`,
    };
  });
};

export const currenciesOptions = getCurrenciesOptions();

/**
 * Deeply get a value from an object via its path.
 */
function getIn(obj: any, key: any, def?: any, p = 0) {
  const path = _.toPath(key);
  while (obj && p < path.length) {
    obj = obj[path[p++]];
  }
  return obj === undefined ? def : obj;
}

export const defaultFastFieldShouldUpdate = (props: any, prevProps: any) => {
  return (
    props.name !== prevProps.name ||
    getIn(props.formik.values, prevProps.name) !==
      getIn(prevProps.formik.values, prevProps.name) ||
    getIn(props.formik.errors, prevProps.name) !==
      getIn(prevProps.formik.errors, prevProps.name) ||
    getIn(props.formik.touched, prevProps.name) !==
      getIn(prevProps.formik.touched, prevProps.name) ||
    Object.keys(prevProps).length !== Object.keys(props).length ||
    props.formik.isSubmitting !== prevProps.formik.isSubmitting
  );
};

export const ensureEntriesHasEmptyLine = R.curry(
  (minLinesNumber: number, defaultEntry: any, entries: any[]) => {
    if (entries.length >= minLinesNumber) {
      return [...entries, defaultEntry];
    }
    return entries;
  },
);

export const transfromViewsToTabs = (views: any[]) => {
  return views.map((view: any) => ({ ..._.pick(view, ['slug', 'name']) }));
};

export function nestedArrayToflatten(
  collection: any[],
  property = 'children',
  parseItem: (a: any, level: any) => any = (a, level) => a,
  level = 1,
) {
  const parseObject = (obj: any) =>
    parseItem(
      {
        ..._.omit(obj, [property]),
        level,
      },
      level,
    );

  return collection.reduce((items: any[], currentValue: any, index: number) => {
    let localItems = [...items];
    const parsedItem = parseObject(currentValue);
    localItems.push(parsedItem);

    if (Array.isArray(currentValue[property])) {
      const flattenArray = nestedArrayToflatten(
        currentValue[property],
        property,
        parseItem,
        level + 1,
      );
      localItems = _.concat(localItems, flattenArray);
    }
    return localItems;
  }, []);
}

export function getFieldsFromResourceMeta(resourceFields: any) {
  const fields = Object.keys(resourceFields)
    .map((fieldKey) => {
      const field = resourceFields[fieldKey];
      const transformedField = {
        ...transformToCamelCase(field),
        key: fieldKey,
      };

      // Translate option labels if the field has options (enumeration type)
      if (transformedField.options && Array.isArray(transformedField.options)) {
        transformedField.options = transformedField.options.map(
          (option: any) => ({
            ...option,
            label:
              typeof option.label === 'string' && option.label.includes('.')
                ? intl.get(option.label, { defaultMessage: option.label })
                : option.label,
          }),
        );
      }

      return transformedField;
    })
    .filter((field: any) => field.filterable !== false);

  return _.orderBy(fields, ['label']);
}

export function getFilterableFieldsFromFields(fields: any[]) {
  return fields.filter((field: any) => field.filterable !== false);
}

export const RESORUCE_TYPE = {
  ACCOUNTS: 'account',
  ITEMS: 'items',
};

function escapeRegExpChars(text: string) {
  return text.replace(/([.*+?^=!:${}()|[\]\\])/g, '\\$1');
}

export function highlightText(text: string, query: string) {
  let lastIndex = 0;
  const words = query
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map(escapeRegExpChars);
  if (words.length === 0) {
    return [text];
  }
  const regexp = new RegExp(words.join('|'), 'gi');
  const tokens = [];
  while (true) {
    const match = regexp.exec(text);
    if (!match) {
      break;
    }
    const length = match[0].length;
    const before = text.slice(lastIndex, regexp.lastIndex - length);
    if (before.length > 0) {
      tokens.push(before);
    }
    lastIndex = regexp.lastIndex;
    tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
  }
  const rest = text.slice(lastIndex);
  if (rest.length > 0) {
    tokens.push(rest);
  }
  return tokens;
}

function isBuffer(obj: any) {
  return (
    obj &&
    obj.constructor &&
    typeof obj.constructor.isBuffer === 'function' &&
    obj.constructor.isBuffer(obj)
  );
}

function keyIdentity(key: any) {
  return key;
}

export function flatten(opts: any, target: any) {
  opts = opts || {};

  const delimiter = opts.delimiter || '.';
  const maxDepth = opts.maxDepth;
  const transformKey = opts.transformKey || keyIdentity;
  const output: Record<string, any> = {};

  function step(object: any, prev?: any, currentDepth?: any) {
    currentDepth = currentDepth || 1;
    Object.keys(object).forEach(function (key) {
      const value = object[key];
      const isarray = opts.safe && Array.isArray(value);
      const type = Object.prototype.toString.call(value);
      const isbuffer = isBuffer(value);
      const isobject = type === '[object Object]' || type === '[object Array]';

      const newKey = prev
        ? prev + delimiter + transformKey(key)
        : transformKey(key);

      if (
        !isarray &&
        !isbuffer &&
        isobject &&
        Object.keys(value).length &&
        (!opts.maxDepth || currentDepth < maxDepth)
      ) {
        return step(value, newKey, currentDepth + 1);
      }

      output[newKey] = value;
    });
  }

  step(target);

  return output;
}

/**
 * Ingores the given selectors from event callback.
 */
export function ignoreEventFromSelectors(event: any, selectors: any[]) {
  return selectors
    .map((selector: any) => event.target.closest(selector))
    .some((element: any) => !!element);
}

export const tableRowTypesToClassnames = ({ original }: any) => {
  const rowTypes = _.castArray(original.row_types);
  const rowId = original.id;

  const rowTypesClsx = rowTypes
    .filter((t: any) => t)
    .map((t: any) => `row_type--${t}`);
  const rowIdClsx = `row-id--${original.id}`;

  return clsx(rowTypesClsx, { [`${rowIdClsx}`]: rowId });
};

/**
 * Filters the given accounts of the given query.
 * @param {*} accounts
 * @param {*} queryProps
 * @returns {*}
 */
export const filterAccountsByQuery = (accounts: any[], queryProps: any) => {
  const defaultQuery = {
    filterByParentTypes: [],
    filterByTypes: [],
    filterByNormal: [],
    filterByRootTypes: [],
    ...pickBy(queryProps, (v) => v !== undefined),
  };
  const query = {
    filterByParentTypes: castArray(defaultQuery.filterByParentTypes),
    filterByTypes: castArray(defaultQuery.filterByTypes),
    filterByNormal: castArray(defaultQuery.filterByNormal),
    filterByRootTypes: castArray(defaultQuery.filterByRootTypes),
  };
  let filteredAccounts = [...accounts];

  if (!isEmpty(query.filterByParentTypes)) {
    filteredAccounts = filteredAccounts.filter((account: any) =>
      includes(query.filterByParentTypes, account.account_parent_type),
    );
  }
  if (!isEmpty(query.filterByTypes)) {
    filteredAccounts = filteredAccounts.filter((account: any) =>
      includes(query.filterByTypes, account.account_type),
    );
  }
  if (!isEmpty(query.filterByNormal)) {
    filteredAccounts = filteredAccounts.filter((account: any) =>
      includes(query.filterByTypes, account.account_normal),
    );
  }
  if (!isEmpty(query.filterByRootTypes)) {
    filteredAccounts = filteredAccounts.filter((account: any) =>
      includes(query.filterByRootTypes, account.account_root_type),
    );
  }
  return filteredAccounts;
};
